import { useEffect, useMemo, useRef, useState } from 'react';
import { PlayIcon, PencilSquareIcon, ShareIcon, StopIcon } from '@heroicons/react/24/outline';
import {
  useRecordings,
  useFolders,
  useTags,
  useSearch,
  useDeleteRecording,
  useRecordingAudioUrl,
  useRecordingTags,
  useUsersByIds,
  useMe,
  type ListRecordingsFilters,
} from '../lib/queries';
import {
  Badge,
  StatusChip,
  InProgressStatus,
  Avatar,
  SearchInput,
  IconButton,
  formatDuration,
  formatEstimatedMinutes,
  Pagination,
  SearchSnippet,
} from '../components/ui';
import { ShareDialog } from '../components/ShareDialog';
import { RecordingProblem } from '../components/RecordingProblem';
import { inProgressPhase, inProgressSince } from '../hooks/useElapsedLabel';
import { useOnDemandEnabled } from '../lib/ondemandQueries';
import { UntranscribedMatches } from '../components/ondemand/UntranscribedMatches';
import { isDeferred } from '@scribe/core/types';
import type { Folder, PageInfo, Recording, RecordingStatus, SearchResult } from '@scribe/core/types';

const STATUS_OPTIONS: RecordingStatus[] = [
  'uploaded',
  'queued',
  'transcribing',
  'completed',
  'failed',
];

/** Cap on tag chips shown per list card (WEBUI-1 #4) — a handful reads fine in the row;
 * a recording with many tags would otherwise push the actions off to the side. */
const MAX_VISIBLE_TAGS = 4;

const NON_TERMINAL_STATUSES = new Set<RecordingStatus>(['uploaded', 'queued', 'transcribing']);

/** How long a completion toast (web-ui-issues #23) stays up before auto-dismissing. */
const TOAST_TTL_MS = 6_000;

interface CompletionToast {
  id: string;
  title: string;
}

/**
 * web-ui-issues #23: a cheap "your recording finished" flourish for whoever's already
 * looking at the list when `useRecordings`'s poll (see lib/queries.ts) picks up a
 * non-terminal -> `completed` transition. Diffs each render's statuses against the
 * previous render's (kept in a ref, not state, so the diff itself never triggers a
 * re-render) rather than subscribing to anything new — the poll that already drives the
 * list is the only signal this needs.
 */
function useCompletionToasts(items: Recording[] | undefined): {
  toasts: CompletionToast[];
  dismiss: (id: string) => void;
} {
  const prevStatusById = useRef<Map<string, RecordingStatus>>(new Map());
  const [toasts, setToasts] = useState<CompletionToast[]>([]);

  useEffect(() => {
    const prev = prevStatusById.current;
    if (items) {
      const justCompleted = items.filter((r) => {
        const before = prev.get(r.id);
        return before !== undefined && NON_TERMINAL_STATUSES.has(before) && r.status === 'completed';
      });
      if (justCompleted.length > 0) {
        setToasts((cur) => [
          ...cur,
          ...justCompleted.map((r) => ({ id: r.id, title: r.title })),
        ]);
      }
      prevStatusById.current = new Map(items.map((r) => [r.id, r.status]));
    }
  }, [items]);

  const dismiss = (id: string) => setToasts((cur) => cur.filter((t) => t.id !== id));
  return { toasts, dismiss };
}

function CompletionToasts({
  toasts,
  onDismiss,
}: {
  toasts: CompletionToast[];
  onDismiss: (id: string) => void;
}) {
  useEffect(() => {
    if (toasts.length === 0) return;
    const timers = toasts.map((t) => setTimeout(() => onDismiss(t.id), TOAST_TTL_MS));
    return () => timers.forEach(clearTimeout);
    // Re-run whenever the *set* of toast ids changes, not on every onDismiss identity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toasts.map((t) => t.id).join(',')]);

  if (toasts.length === 0) return null;
  return (
    <div className="toast-stack" role="status" aria-live="polite">
      {toasts.map((t) => (
        <div className="toast" key={t.id}>
          <p>“{t.title}” finished transcribing.</p>
          <button className="ghost sm" onClick={() => onDismiss(t.id)}>
            Dismiss
          </button>
        </div>
      ))}
    </div>
  );
}

export function RecordingsList({
  onOpen,
  scope = 'all',
  folderId,
  tag,
  onFilterChange = () => {},
}: {
  onOpen: (id: string) => void;
  /** SHARE-4: `'shared'` renders the "Shared with me" nav entry's view (isolated,
   * paginated scope filter); default `'all'` is the main Recordings list (owned ∪ shared,
   * unchanged from pre-SHARE-4 behavior). */
  scope?: 'all' | 'shared';
  /** SHARE-5: folder/tag filter state now lives with the caller (App.tsx's Shell, sourced
   * from the URL — see routes.ts's View) so the rail and these dropdowns are two views over
   * one state ("dropdowns follow") rather than two independent states. Omitted for the
   * `/shared` scope, which doesn't get rail filters in M10 (decision 5, docs/specs/share-5.md). */
  folderId?: string | undefined;
  tag?: string | undefined;
  /** Called when a dropdown changes; omitted (defaults to a no-op) for `/shared`. */
  onFilterChange?: (f: { folderId?: string | undefined; tag?: string | undefined }) => void;
}) {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<RecordingStatus | undefined>(undefined);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [shareId, setShareId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [searchPage, setSearchPage] = useState(1);

  const filters: ListRecordingsFilters = useMemo(
    () => ({ folderId, tags: tag ? [tag] : undefined, status, scope, page }),
    [folderId, tag, status, scope, page],
  );

  const { data: folders } = useFolders();
  const { data: tags } = useTags();
  const { data: recordings, isLoading, isFetching, error, refetch } = useRecordings(filters);
  const search = useSearch(q, searchPage);
  const deleteRecording = useDeleteRecording();
  // SHARE-4/#20: the caller's own platform id, so cards can tell owned rows from shared-in
  // ones (`recording.owner_id !== meId`) regardless of which scope is being viewed —
  // `undefined` while `useMe` is loading/erroring deliberately suppresses the indicator
  // rather than guessing (see `RecordingCard`'s `isShared` below).
  const { data: me } = useMe();

  const folderById = useMemo(() => {
    const map = new Map<string, Folder>();
    for (const f of folders?.items ?? []) map.set(f.id, f);
    return map;
  }, [folders]);

  // SHARE-2/#7: one batched directory lookup for the whole visible page's owner ids, at the
  // screen level (NOT one per card) — resolves the raw owner uuid the avatar used to render
  // (and title) into a real display name.
  const ownerIds = useMemo(() => (recordings?.items ?? []).map((r) => r.owner_id), [recordings]);
  const { byId: usersById } = useUsersByIds(ownerIds);

  const searching = q.trim().length > 0;
  // ONDEMAND-1: deferred recordings are invisible to full-text search (no transcript yet), so
  // with the feature on a search also lists title/tag/folder matches that aren't transcribed.
  const onDemandEnabled = useOnDemandEnabled();

  // Any filter change should reset back to page 1 — a stale page number for a narrower
  // filter set could otherwise land past the new result set's last page.
  useEffect(() => {
    setPage(1);
  }, [folderId, tag, status]);

  useEffect(() => {
    setSearchPage(1);
  }, [q]);

  const dayGroups = useMemo(() => groupByDay(recordings?.items ?? []), [recordings]);
  const { toasts, dismiss: dismissToast } = useCompletionToasts(recordings?.items);

  return (
    <div className="card">
      <div className="card-head">
        <h1>{scope === 'shared' ? 'Shared with me' : 'Recordings'}</h1>
      </div>
      <p className="muted">
        Search transcripts, or filter your recordings by folder, tag, and upload status.
      </p>

      <div className="toolbar">
        <SearchInput
          value={q}
          onChange={setQ}
          placeholder="Search transcript text…"
          ariaLabel="Search transcript text"
        />
        <select
          aria-label="Filter by folder"
          value={folderId ?? ''}
          onChange={(e) => onFilterChange({ folderId: e.target.value || undefined, tag })}
        >
          <option value="">All folders</option>
          {folders?.items.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </select>
        <select
          aria-label="Filter by tag"
          value={tag ?? ''}
          onChange={(e) => onFilterChange({ folderId, tag: e.target.value || undefined })}
        >
          <option value="">All tags</option>
          {tags?.items.map((t) => (
            <option key={t.id} value={t.name}>
              {t.name}
            </option>
          ))}
        </select>
        <select
          aria-label="Filter by status"
          value={status ?? ''}
          onChange={(e) => setStatus((e.target.value || undefined) as RecordingStatus | undefined)}
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {searching && onDemandEnabled && <UntranscribedMatches q={q} onOpen={onOpen} />}
      {searching ? (
        <SearchResults
          results={search.data?.items}
          pageInfo={search.data?.page_info}
          loading={search.isLoading}
          error={search.error as Error | null}
          onOpen={onOpen}
          onPageChange={setSearchPage}
          onRetry={() => void search.refetch()}
        />
      ) : (
        <>
          {error && (
            <div className="banner banner-danger">
              <p>{(error as Error).message || 'Failed to load recordings.'}</p>
              <button className="sm" onClick={() => void refetch()}>
                Retry
              </button>
            </div>
          )}
          {isLoading && <p className="muted">Loading recordings…</p>}
          {recordings && (
            <>
              {recordings.items.length === 0 ? (
                <p className="empty-state">
                  {folderId || tag || status
                    ? 'No recordings match your filters.'
                    : scope === 'shared'
                      ? 'Nothing shared with you yet.'
                      : 'No recordings yet — record or upload one to get started.'}
                </p>
              ) : (
                <div className="feed" aria-busy={isFetching}>
                  {dayGroups.map((group) => (
                    <section key={group.key} aria-label={group.label}>
                      <div className="daylabel">
                        {group.label}
                        <span className="n">
                          {group.items.length} recording{group.items.length === 1 ? '' : 's'} ·{' '}
                          {formatDuration(group.totalSec)}
                        </span>
                      </div>
                      {group.items.map((r) => (
                        <RecordingCard
                          key={r.id}
                          recording={r}
                          folder={r.folder_id ? folderById.get(r.folder_id) : undefined}
                          ownerName={usersById.get(r.owner_id)?.display_name}
                          meId={me?.id}
                          onOpen={() => onOpen(r.id)}
                          onShare={() => setShareId(r.id)}
                          confirmDelete={confirmDelete === r.id}
                          onRequestDelete={() => setConfirmDelete(r.id)}
                          onCancelDelete={() => setConfirmDelete(null)}
                          onConfirmDelete={() =>
                            deleteRecording.mutate(r.id, {
                              onSettled: () => setConfirmDelete(null),
                            })
                          }
                          deleting={deleteRecording.isPending}
                        />
                      ))}
                    </section>
                  ))}
                </div>
              )}
              <Pagination pageInfo={recordings.page_info} onPageChange={setPage} />
              {deleteRecording.isError && (
                <p className="error">{(deleteRecording.error as Error).message}</p>
              )}
            </>
          )}
        </>
      )}

      {shareId && (
        <ShareDialog resourceType="recording" resourceId={shareId} onClose={() => setShareId(null)} />
      )}
      <CompletionToasts toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}

function RecordingCard({
  recording,
  folder,
  ownerName,
  meId,
  onOpen,
  onShare,
  confirmDelete,
  onRequestDelete,
  onCancelDelete,
  onConfirmDelete,
  deleting,
}: {
  recording: Recording;
  folder: Folder | undefined;
  /** Owner's resolved display name (SHARE-2/#7), or `undefined` while the batched directory
   * lookup is loading or the id is unknown — never the raw `owner_id` uuid. */
  ownerName: string | undefined;
  /** SHARE-4/#20: the caller's own platform id, or `undefined` while `useMe` hasn't
   * resolved yet — `undefined` suppresses the Shared chip rather than showing it on every
   * card (see `isShared` below). */
  meId: string | undefined;
  onOpen: () => void;
  onShare: () => void;
  confirmDelete: boolean;
  onRequestDelete: () => void;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
  deleting: boolean;
}) {
  // SHARE-4/#20: "shared" means shared *with* me (incoming) — an owned row is never
  // flagged, even in the shared-with-me view (which by construction excludes them anyway).
  const isShared = meId != null && recording.owner_id !== meId;
  const time = new Date(recording.created_at).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });

  const [playing, setPlaying] = useState(false);
  // Defense in depth against a stale/optimistic `audio_available`: if the <audio>
  // element itself fails to load the presigned URL (the master object doesn't actually
  // exist yet — e.g. mid-upload/mid-live-capture), swap to an honest "not available yet"
  // note instead of a dead player. Keyed by URL so a later, fresh presign retries.
  const [failedAudioUrl, setFailedAudioUrl] = useState<string | null>(null);
  const {
    data: audio,
    isLoading: audioLoading,
    isError: audioIsError,
  } = useRecordingAudioUrl(recording.id, playing);
  const { data: tags } = useRecordingTags(recording.id);

  return (
    <article className="recording-card">
      <div className="rc-head">
        <div className="rc-title-block">
          <h3>
            <a
              role="button"
              tabIndex={0}
              onClick={onOpen}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onOpen();
                }
              }}
            >
              {recording.title}
            </a>
          </h3>
          <div className="rc-meta">
            <span className="mono">{time}</span>
            <span aria-hidden="true">·</span>
            {/* Always render the duration slot so the row layout is stable. '—' means
                duration_sec is still null upstream (issue #5 — a backend fix, populating
                duration_sec after transcription — out of this frontend-only scope). */}
            {recording.duration_sec == null && recording.estimated_duration_sec != null ? (
              // ONDEMAND-1: a deferred recording has no measured duration yet — show the
              // server's size-based estimate, marked as one.
              <span className="mono" title="Estimated duration">
                {formatEstimatedMinutes(recording.estimated_duration_sec)}
              </span>
            ) : (
              <span className="mono">
                {recording.duration_sec != null ? formatDuration(recording.duration_sec) : '—'}
              </span>
            )}
            {folder && (
              <>
                <span aria-hidden="true">·</span>
                <span>{folder.name}</span>
              </>
            )}
          </div>
        </div>
        {/* web-ui-issues #7: never fall back to the raw owner uuid — while loading or for
            an unknown id, a neutral '?' avatar with no misleading tooltip. */}
        <Avatar name={ownerName ?? '?'} />
      </div>

      <div className="rc-row">
        <RecordingStatusChip status={recording.status} recording={recording} />
        {isShared && (
          <span className="tag" aria-label={'Shared by ' + (ownerName ?? 'someone')}>
            Shared
          </span>
        )}
        {/* One small per-card request for tag chips (mirrors the detail view's
            client.getRecordingTags), cached with a long staleTime and not polled — fine at
            current page sizes; batching/embedding tags on the list response is a later,
            out-of-scope optimization. */}
        {tags?.slice(0, MAX_VISIBLE_TAGS).map((t) => (
          <span key={t.id} className="tag">
            #{t.name}
          </span>
        ))}

        <div className="rc-actions">
          {/* web-ui-issues #15: gate on `audio_available`, not `status` — the durable
              master exists as soon as capture finalizes, well before the recording
              reaches `completed`, so a still-transcribing recording can already preview. */}
          <IconButton
            label={playing ? 'Stop preview' : recording.audio_available ? 'Play' : 'Audio not available yet'}
            title={recording.audio_available ? undefined : 'Audio not available yet'}
            disabled={!playing && !recording.audio_available}
            onClick={() => setPlaying((p) => !p)}
          >
            {playing ? (
              <StopIcon width={20} height={20} aria-hidden="true" />
            ) : (
              <PlayIcon width={20} height={20} aria-hidden="true" />
            )}
          </IconButton>
          <IconButton label="Edit transcript" onClick={onOpen}>
            <PencilSquareIcon width={20} height={20} aria-hidden="true" />
          </IconButton>
          <IconButton label="Share" onClick={onShare}>
            <ShareIcon width={20} height={20} aria-hidden="true" />
          </IconButton>
          {confirmDelete ? (
            <>
              <button className="danger sm" disabled={deleting} onClick={onConfirmDelete}>
                Confirm
              </button>
              <button className="ghost sm" onClick={onCancelDelete}>
                Cancel
              </button>
            </>
          ) : (
            <button className="ghost sm" onClick={onRequestDelete}>
              Delete
            </button>
          )}
        </div>
      </div>

      {playing && (
        <div className="audio-bar">
          {audioLoading && <p className="muted">Loading preview…</p>}
          {!audioLoading && audioIsError && <p className="error">Couldn't load audio preview.</p>}
          {!audioLoading && !audioIsError && audio?.url && failedAudioUrl === audio.url && (
            <p className="muted">Audio not available yet.</p>
          )}
          {!audioLoading && !audioIsError && audio?.url && failedAudioUrl !== audio.url && (
            <audio
              controls
              autoPlay
              src={audio.url}
              style={{ width: '100%' }}
              onError={() => setFailedAudioUrl(audio.url)}
            />
          )}
        </div>
      )}
    </article>
  );
}

/**
 * web-ui-issues #16: honest, status-driven treatment for a card's pipeline state — no
 * fabricated percent bar (AWS Transcribe batch reports no completion fraction). `uploaded`
 * hasn't entered the pipeline yet (RecordingDetail's manual "Transcribe" banner is how it
 * gets there), so it gets a plain, non-spinning chip rather than implying active work that
 * isn't happening; `queued`/`transcribing` — genuinely in flight — get `InProgressStatus`'s
 * spinner, with its label driven by `inProgressPhase` rather than the raw status (§2.12):
 * `transcribing` alone cannot tell an active capture from the post-stop finalize from a real
 * job, so the chip reads "Recording…" / "Finalizing audio…" / "Transcribing… <elapsed>"
 * accordingly. The elapsed appears only for the phases that have an honest anchor — nothing
 * records when capture ended, and the old `?? created_at` fallback is exactly what made a
 * just-stopped meeting read roughly its own length and climb.
 */
function RecordingStatusChip({
  status,
  recording,
}: {
  status: RecordingStatus;
  recording: Recording;
}) {
  if (status === 'completed') return <StatusChip variant="done" />;
  // ONDEMAND-1: deferred = waiting for someone to want it, not work in flight — neutral, no
  // spinner. Only possible when the feature is on (`transcription_deferred_at` is null otherwise).
  if (isDeferred(recording)) return <Badge kind="neutral">Not transcribed</Badge>;
  if (status === 'failed') {
    // ERRORS-1: say WHY at a glance when the server recorded a reason (the chip alone is
    // unchanged for legacy rows); the full explanation + fix lives on the detail screen.
    return (
      <>
        <StatusChip variant="failed" />
        {recording.failure_code && (
          <RecordingProblem variant="inline" code={recording.failure_code} />
        )}
      </>
    );
  }
  if (status === 'queued' || status === 'transcribing') {
    return <InProgressStatus phase={inProgressPhase(recording)} since={inProgressSince(recording)} />;
  }
  return <StatusChip variant="prog">Uploaded</StatusChip>;
}

interface DayGroup {
  key: string;
  label: string;
  items: Recording[];
  totalSec: number;
}

/** Buckets recordings by calendar day (Today / Yesterday / date), newest day first, items
 * within a day kept in the order the API returned them. */
function groupByDay(items: Recording[]): DayGroup[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const groups = new Map<string, DayGroup>();
  for (const r of items) {
    const created = new Date(r.created_at);
    const dayStart = new Date(created);
    dayStart.setHours(0, 0, 0, 0);
    const key = dayStart.toISOString();

    let label: string;
    if (dayStart.getTime() === today.getTime()) label = 'Today';
    else if (dayStart.getTime() === yesterday.getTime()) label = 'Yesterday';
    else label = dayStart.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });

    let group = groups.get(key);
    if (!group) {
      group = { key, label, items: [], totalSec: 0 };
      groups.set(key, group);
    }
    group.items.push(r);
    group.totalSec += r.duration_sec ?? 0;
  }

  return Array.from(groups.values()).sort((a, b) => (a.key < b.key ? 1 : -1));
}

function SearchResults({
  results,
  pageInfo,
  loading,
  error,
  onOpen,
  onPageChange,
  onRetry,
}: {
  results: SearchResult[] | undefined;
  pageInfo: PageInfo | undefined;
  loading: boolean;
  error: Error | null;
  onOpen: (id: string) => void;
  onPageChange: (page: number) => void;
  onRetry: () => void;
}) {
  if (loading) return <p className="muted">Searching…</p>;
  if (error) {
    return (
      <div className="banner banner-danger">
        <p>{error.message || 'Search failed.'}</p>
        <button className="sm" onClick={onRetry}>
          Retry
        </button>
      </div>
    );
  }
  if (!results || results.length === 0) return <p className="empty-state">No matches.</p>;
  return (
    <>
      <div className="feed">
        {results.map((res) => (
          <article className="recording-card" key={res.segment_id}>
            <div className="rc-head">
              <div className="rc-title-block">
                <h3>
                  <a
                    role="button"
                    tabIndex={0}
                    onClick={() => onOpen(res.recording_id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onOpen(res.recording_id);
                      }
                    }}
                  >
                    {res.recording_title ?? res.recording_id}
                  </a>
                </h3>
                <div className="rc-meta">
                  {res.speaker_label && <span>{res.speaker_label}</span>}
                  <span aria-hidden="true">·</span>
                  <span className="mono">{res.score.toFixed(2)}</span>
                </div>
              </div>
            </div>
            <p className="snip">
              <SearchSnippet snippet={res.snippet} />
            </p>
          </article>
        ))}
      </div>
      {pageInfo && <Pagination pageInfo={pageInfo} onPageChange={onPageChange} />}
    </>
  );
}
